import { GetAllChamberMembersQuery } from "../../../graphql/types";

type GetAllChamberMembers = NonNullable<
  GetAllChamberMembersQuery["getAllChamberMembers"]
>;

type GetAllChamberMember = GetAllChamberMembers[number];

export type MemberDirectoryExportSource = Pick<
  GetAllChamberMember,
  | "name"
  | "adminConsoleContactName"
  | "adminConsoleLoginEmail"
  | "contactName"
  | "email"
  | "isSuspended"
  | "hasSignedUp"
> & {
  address?: Partial<GetAllChamberMember["address"]> | null;
  categoryId?: ReadonlyArray<string | null | undefined> | null;
};

export type MemberDirectoryExportRow = {
  memberName: string;
  streetAddress: string;
  city: string;
  province: string;
  postalCode: string;
  memberDashboardContactName: string;
  memberDashboardContactEmail: string;
  companyContactName: string;
  companyContactEmail: string;
  membershipStatus: "Active" | "Suspended";
  category1: string;
  category2: string;
  hasSignedUp: "Yes" | "No";
};

export type MemberDirectoryExportHeader = {
  label: string;
  key: keyof MemberDirectoryExportRow;
};

// Keep the existing ten-column prefix stable for chambers that import this CSV.
// Restored fields are appended in their historical order.
export const MEMBER_DIRECTORY_EXPORT_HEADERS: MemberDirectoryExportHeader[] = [
  { label: "Member Name", key: "memberName" },
  { label: "Street Address", key: "streetAddress" },
  { label: "City", key: "city" },
  { label: "Province", key: "province" },
  { label: "Postal Code", key: "postalCode" },
  {
    label: "Member Dashboard Contact Name",
    key: "memberDashboardContactName",
  },
  {
    label: "Member Dashboard Contact Email",
    key: "memberDashboardContactEmail",
  },
  { label: "Company Contact Name", key: "companyContactName" },
  { label: "Company Contact Email", key: "companyContactEmail" },
  { label: "Membership Status", key: "membershipStatus" },
  { label: "Category 1", key: "category1" },
  { label: "Category 2", key: "category2" },
  { label: "Has Logged In To Dashboard", key: "hasSignedUp" },
];

const getCategoryExportValue = ({
  categoryId,
  categoryNameById,
}: {
  categoryId: string | null | undefined;
  categoryNameById?: Readonly<Record<string, string>> | null;
}): string => {
  if (!categoryId) {
    return "";
  }

  // Preserve an unknown/legacy ID instead of silently dropping export data.
  return categoryNameById?.[categoryId] || categoryId;
};

export const buildMemberDirectoryExportRows = ({
  members,
  categoryNameById,
}: {
  members: ReadonlyArray<MemberDirectoryExportSource>;
  categoryNameById?: Readonly<Record<string, string>> | null;
}): MemberDirectoryExportRow[] =>
  members.map((member) => ({
    memberName: member.name,
    streetAddress: member.address?.streetAddress || "",
    city: member.address?.city || "",
    province: member.address?.province || "",
    postalCode: member.address?.postalCode || "",
    memberDashboardContactName: member.adminConsoleContactName || "",
    memberDashboardContactEmail: member.adminConsoleLoginEmail || "",
    companyContactName: member.contactName || "",
    companyContactEmail: member.email || "",
    membershipStatus: member.isSuspended ? "Suspended" : "Active",
    category1: getCategoryExportValue({
      categoryId: member.categoryId?.[0],
      categoryNameById,
    }),
    category2: getCategoryExportValue({
      categoryId: member.categoryId?.[1],
      categoryNameById,
    }),
    hasSignedUp: member.hasSignedUp ? "Yes" : "No",
  }));
